import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { EntityResponse } from "../../../../api";
import { useClientEntityCandidates } from "../../../../api/hooks";
import { CandidateStatus, CandidateQualification, displayQualification } from "../../../../api/models/EntityCandidate";
import { Badge, Box, Card, CardBody, Flex, Grid, Heading, Icon, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { Shimmer } from "@am-tax/fe-core";
import { PercentPie } from "../../../ClientDashboardPage/PercentPie";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { IconBuildingWarehouse, IconClipboardCheck, IconShieldCheck, IconUsers } from "@tabler/icons-react";

// ── Status classification sets ────────────────────────────────────────────────
const SCREENED_STATUSES = new Set<CandidateStatus>([
    CandidateStatus.NotEligible,
    CandidateStatus.Eligible,
    CandidateStatus.PendingCertification,
    CandidateStatus.Certified,
    CandidateStatus.Denied,
    CandidateStatus.InAppeal,
    CandidateStatus.AppealDenied,
    CandidateStatus.Claimed,
    CandidateStatus.PendingDocuments,
    CandidateStatus.UnableToSubstantiate,
    CandidateStatus.ReadyForCertification,
]);

const NOT_ELIGIBLE_STATUSES = new Set<CandidateStatus>([
    CandidateStatus.NotScreened,
    CandidateStatus.Screening,
    CandidateStatus.OptedOut,
    CandidateStatus.NotEligible,
    CandidateStatus.UnableToSubstantiate,
    CandidateStatus.Denied,
    CandidateStatus.AppealDenied,
]);

const NOT_HIRED_STATUSES = new Set<CandidateStatus>([...NOT_ELIGIBLE_STATUSES, CandidateStatus.NotHired]);

// ── KPI stat card ─────────────────────────────────────────────────────────────
type StatCardProps = {
    title: string;
    value: number | string;
    sub?: string;
    accentColor: string;
    icon: FC<{ size?: number; strokeWidth?: number }>;
    isLoading: boolean;
    onClick?: () => void;
};

const StatCard: FC<StatCardProps> = ({ title, value, sub, accentColor, icon, isLoading, onClick }) => (
    <Shimmer isLoading={isLoading} height="100px">
        <Card
            variant="withShadow"
            overflow="hidden"
            h="100px"
            onClick={onClick}
            cursor={onClick ? "pointer" : undefined}
            _hover={onClick ? { boxShadow: "md", borderColor: "blue.200" } : undefined}
            transition="box-shadow 0.15s, border-color 0.15s"
        >
            <Box h="3px" bg={accentColor} />
            <CardBody pt={3} pb={3}>
                <Flex justify="space-between" align="flex-start">
                    <VStack align="flex-start" spacing={1}>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                            {title}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" lineHeight={1} color="gray.800">
                            {value}
                        </Text>
                        {sub && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                {sub}
                            </Text>
                        )}
                    </VStack>
                    <Icon as={icon} boxSize={6} color={accentColor} opacity={0.6} />
                </Flex>
            </CardBody>
        </Card>
    </Shimmer>
);

// ── Application status chart entries ─────────────────────────────────────────
const STATUS_CHART_ENTRIES: Array<{ label: string; statuses: CandidateStatus[]; color: string }> = [
    {
        label: "Apps Submitted",
        statuses: [...SCREENED_STATUSES],
        color: "var(--chakra-colors-blue-800)",
    },
    {
        label: "Certifications Pending",
        statuses: [CandidateStatus.PendingCertification],
        color: "var(--chakra-colors-cyan-500)",
    },
    {
        label: "Certifications Received",
        statuses: [CandidateStatus.Certified, CandidateStatus.Claimed],
        color: "var(--chakra-colors-green-500)",
    },
    {
        label: "Appeal Pending",
        statuses: [CandidateStatus.InAppeal],
        color: "var(--chakra-colors-purple-500)",
    },
    {
        label: "Appeal Denied",
        statuses: [CandidateStatus.AppealDenied],
        color: "var(--chakra-colors-orange-500)",
    },
];

// ── Main component ────────────────────────────────────────────────────────────
type EntityOverviewTabProps = {
    clientId: string;
    entity: EntityResponse;
};

export const EntityOverviewTab: FC<EntityOverviewTabProps> = ({ clientId, entity }) => {
    const navigate = useNavigate();
    const candidatesQuery = useClientEntityCandidates(clientId, entity.id, true, false);
    const candidates = candidatesQuery.data ?? [];
    const isLoading = candidatesQuery.isLoading;

    const stats = useMemo(() => {
        const total = candidates.length;
        const screened = candidates.filter(c => SCREENED_STATUSES.has(c.status)).length;
        const eligible = candidates.filter(c => !NOT_ELIGIBLE_STATUSES.has(c.status)).length;
        const hired = candidates.filter(c => !NOT_HIRED_STATUSES.has(c.status)).length;
        const questionnairesCompleted = candidates.filter(c => !!c.questionnaireCompletedOn).length;
        return { total, screened, eligible, hired, questionnairesCompleted };
    }, [candidates]);

    const statusChartData = useMemo(
        () =>
            STATUS_CHART_ENTRIES.map(({ label, statuses }) => ({
                name: label,
                value: candidates.filter(c => statuses.includes(c.status)).length,
            })),
        [candidates]
    );

    const qualChartData = useMemo(() => {
        const counts: Record<string, number> = {};
        candidates.forEach(c => {
            if (c.selectedQualification) {
                counts[c.selectedQualification] = (counts[c.selectedQualification] ?? 0) + 1;
            }
        });
        return Object.entries(counts)
            .map(([key, value]) => ({
                name: displayQualification(key as CandidateQualification),
                value,
            }))
            .sort((a, b) => b.value - a.value);
    }, [candidates]);

    const hasQualData = qualChartData.length > 0;

    if (!isLoading && stats.total === 0) {
        return (
            <Flex direction="column" align="center" justify="center" py={12} gap={2} color="gray.400">
                <Icon as={IconUsers} boxSize={10} />
                <Text fontSize="md" fontWeight="medium">
                    No candidates yet
                </Text>
                <Text fontSize="sm">Candidates who apply through this entity's survey link will appear here.</Text>
            </Flex>
        );
    }

    return (
        <VStack align="stretch" spacing="1.25rem">
            {/* KPI cards */}
            <SimpleGrid columns={{ base: 2, lg: 4 }} spacing="1rem">
                <StatCard
                    title="Total Candidates"
                    value={stats.total}
                    accentColor="var(--chakra-colors-blue-500)"
                    icon={IconUsers}
                    isLoading={isLoading}
                    onClick={() => navigate(`/clients/${clientId}/candidates?eId=${entity.id}`)}
                />
                <StatCard
                    title="Hired"
                    value={stats.hired}
                    sub={stats.total > 0 ? `${Math.round((stats.hired / stats.total) * 100)}% hire rate` : undefined}
                    accentColor="var(--chakra-colors-teal-500)"
                    icon={IconBuildingWarehouse}
                    isLoading={isLoading}
                />
                <StatCard
                    title="Screened"
                    value={stats.screened}
                    sub={stats.total > 0 ? `out of ${stats.total}` : undefined}
                    accentColor="var(--chakra-colors-cyan-500)"
                    icon={IconShieldCheck}
                    isLoading={isLoading}
                />
                <StatCard
                    title="Questionnaires"
                    value={stats.questionnairesCompleted}
                    sub={stats.total > 0 ? `${Math.round((stats.questionnairesCompleted / stats.total) * 100)}% completion` : undefined}
                    accentColor="var(--chakra-colors-purple-500)"
                    icon={IconClipboardCheck}
                    isLoading={isLoading}
                />
            </SimpleGrid>

            {/* Success rates + Application status */}
            <Grid templateColumns={{ base: "1fr", xl: "2fr 3fr" }} gap="1.25rem">
                <Card variant="withShadow">
                    <CardBody px="1.25rem" pt="1rem">
                        <Heading variant="cardHeading" mb=".5rem">
                            Success Rates
                        </Heading>
                        <Flex wrap="wrap" justify="space-around" gap={4} pt={2}>
                            <PercentPie
                                isLoading={isLoading}
                                color="var(--chakra-colors-blue-600)"
                                target={stats.screened}
                                total={stats.total}
                                title="Screened"
                                subtitle="[Screened] / [Total] × 100"
                                description="Screened candidates have advanced past the initial screening statuses."
                            />
                            <PercentPie
                                isLoading={isLoading}
                                color="var(--chakra-colors-cyan-500)"
                                target={stats.eligible}
                                total={stats.total}
                                title="Eligible"
                                subtitle="[Eligible] / [Total] × 100"
                                description="Eligible candidates have statuses not in: Not Screened, Screening, Opted Out, Not Eligible, Unable to Substantiate, Denied, Appeal Denied."
                            />
                            <PercentPie
                                isLoading={isLoading}
                                color="var(--chakra-colors-teal-500)"
                                target={stats.hired}
                                total={stats.total}
                                title="Hired"
                                subtitle="[Hired] / [Total] × 100"
                                description="Hired candidates have been actively employed at this location."
                            />
                        </Flex>
                    </CardBody>
                </Card>

                <Card variant="withShadow">
                    <CardBody px="1.25rem" pt="1rem">
                        <Heading variant="cardHeading" mb=".5rem">
                            Application Status
                        </Heading>
                        <Shimmer isLoading={isLoading} width="100%" height={240}>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={statusChartData} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 4 }} barSize={16}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                    <YAxis type="category" dataKey="name" width={155} tick={{ fontSize: 12 }} />
                                    <Tooltip cursor={{ fill: "var(--chakra-colors-gray-50)" }} />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                        {STATUS_CHART_ENTRIES.map(({ color }, i) => (
                                            <Cell key={i} fill={color} />
                                        ))}
                                        <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 600, fill: "#555" }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Shimmer>
                    </CardBody>
                </Card>
            </Grid>

            {/* WOTC category breakdown */}
            {(isLoading || hasQualData) && (
                <Card variant="withShadow">
                    <CardBody px="1.25rem" pt="1rem" pb="1.5rem">
                        <Heading variant="cardHeading" mb=".5rem">
                            WOTC Categories
                        </Heading>
                        <Shimmer isLoading={isLoading} width="100%" height={Math.max(160, qualChartData.length * 36 + 20)}>
                            <ResponsiveContainer width="100%" height={Math.max(160, qualChartData.length * 36 + 20)}>
                                <BarChart data={qualChartData} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 4 }} barSize={20}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                    <YAxis type="category" dataKey="name" width={240} tick={{ fontSize: 12 }} />
                                    <Tooltip cursor={{ fill: "var(--chakra-colors-gray-50)" }} />
                                    <Bar dataKey="value" fill="var(--chakra-colors-blue-400)" radius={[0, 4, 4, 0]}>
                                        <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 600, fill: "#555" }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Shimmer>
                        {!isLoading && (
                            <Flex gap={2} flexWrap="wrap" mt={3}>
                                {qualChartData.map(({ name, value }) => (
                                    <Badge key={name} colorScheme="blue" variant="subtle" borderRadius="full" px={2}>
                                        {name}: {value}
                                    </Badge>
                                ))}
                            </Flex>
                        )}
                    </CardBody>
                </Card>
            )}
        </VStack>
    );
};
